'use client';

import React from 'react';

import { Paragraph } from '../ui/paragraph';
import { Button } from '../ui/button';

const LINE_HEIGHT_PX = 24;
const MAX_LINES = 3;
const COLLAPSED_MAX_HEIGHT = LINE_HEIGHT_PX * MAX_LINES;

export function CategoryDescription({ description }: { description: string }) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isClamped, setIsClamped] = React.useState(false);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const contentId = React.useId();

  React.useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const measure = () => setIsClamped(el.scrollHeight > COLLAPSED_MAX_HEIGHT);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [description]);

  return (
    <div className="flex w-full flex-col items-start justify-start gap-4">
      <div
        id={contentId}
        className="w-full overflow-hidden transition-all duration-300 ease-in-out"
        style={{ maxHeight: isExpanded ? 'none' : COLLAPSED_MAX_HEIGHT }}
      >
        <div ref={contentRef}>
          <Paragraph
            innerHtml={description}
            color="muted-foreground"
            size="base"
            className="w-full"
          />
        </div>
      </div>

      <div className="h-6">
        <Button
          type="button"
          variant="none"
          size="none"
          aria-expanded={isExpanded}
          aria-controls={contentId}
          aria-hidden={!isClamped}
          disabled={!isClamped}
          tabIndex={isClamped ? 0 : -1}
          onClick={() => setIsExpanded(prev => !prev)}
          className="font-bold uppercase"
          style={{ visibility: isClamped ? 'visible' : 'hidden' }}
        >
          {isExpanded ? 'Ukázať menej' : 'Ukázať viac'}
        </Button>
      </div>
    </div>
  );
}
